import { ChevronDown, EyeOff, FileSignature, Lock, Users } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { companyPath } from "~/lib/company-profile";
import { formatAmountWithCurrency, formatDate, formatPersonName } from "~/lib/format";
import { ROLE_LABELS } from "~/lib/officer-roles";
import { PERSON_REDACTED_NOTICE, personPath } from "~/lib/person-profile";
import type { Officer, PersonSummary, Shareholder, ViewerTier } from "~/types/company";

const ROLE_GROUPS: { key: string; label: string; roles: string[] }[] = [
	{
		key: "jednatele",
		label: "Jednatelé",
		roles: ["STATUTORY_DIRECTOR"],
	},
	{
		key: "predstavenstvo",
		label: "Představenstvo",
		roles: ["BOARD_MEMBER", "BOARD_CHAIR"],
	},
	{
		key: "dozorci-rada",
		label: "Dozorčí rada",
		roles: ["SUPERVISORY_BOARD_MEMBER", "SUPERVISORY_BOARD_CHAIR"],
	},
	{
		key: "ostatni",
		label: "Ostatní",
		roles: ["PROXY", "LIQUIDATOR", "MANAGING_DIRECTOR"],
	},
];

interface Props {
	officers: Officer[];
	shareholders: Shareholder[];
	officersInactiveCount: number;
	shareholdersInactiveCount: number;
	viewerTier: ViewerTier;
	zpusobJednani?: string | null;
	pocetClenu?: number | null;
}

function getInitials(person: PersonSummary): string {
	const first = person.firstName?.[0] ?? "";
	const last = person.lastName?.[0] ?? "";
	return (first + last).toUpperCase();
}

function getInitialsFromName(name: string): string {
	const parts = name.trim().split(/\s+/);
	if (parts.length >= 2) {
		return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
	}
	return name.substring(0, 2).toUpperCase();
}

function groupOfficersByRole(officers: Officer[]) {
	const groups: { key: string; label: string; officers: Officer[] }[] = [];
	const assigned = new Set<string>();

	for (const group of ROLE_GROUPS) {
		const matching = officers.filter((o) => group.roles.includes(o.role));
		if (matching.length > 0) {
			// Sort: active first, then by appointed date desc
			matching.sort((a, b) => {
				if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
				return 0;
			});
			groups.push({ key: group.key, label: group.label, officers: matching });
			for (const o of matching) assigned.add(o.id);
		}
	}

	// Catch any officers with unknown roles
	const unmatched = officers.filter((o) => !assigned.has(o.id));
	if (unmatched.length > 0) {
		unmatched.sort((a, b) => {
			if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
			return 0;
		});
		groups.push({ key: "neznama-role", label: "Další funkce", officers: unmatched });
	}

	return groups;
}

export function PeopleTab({
	officers,
	shareholders,
	officersInactiveCount,
	shareholdersInactiveCount,
	viewerTier,
	zpusobJednani,
	pocetClenu,
}: Props) {
	const isAnon = viewerTier === "anon";
	const activeShareholders = shareholders.filter((s) => s.isActive);
	const inactiveShareholders = shareholders.filter((s) => !s.isActive);
	const [showInactive, setShowInactive] = useState(false);
	const officerGroups = groupOfficersByRole(officers);

	return (
		<div className="flex flex-col gap-6 pt-4">
			{/* Způsob jednání banner */}
			{(zpusobJednani || pocetClenu != null) && (
				<div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
					<FileSignature className="h-4 w-4 shrink-0 text-primary-600 mt-0.5" />
					<div className="flex flex-col gap-1 text-sm">
						{zpusobJednani && (
							<p>
								<span className="font-medium">Způsob jednání:</span>{" "}
								<span className="text-muted-foreground">{zpusobJednani}</span>
							</p>
						)}
						{pocetClenu != null && (
							<p>
								<span className="font-medium">Počet členů orgánu:</span>{" "}
								<span className="text-muted-foreground">{pocetClenu}</span>
							</p>
						)}
					</div>
				</div>
			)}

			{/* Officers section */}
			<div className="card-surface rounded-xl p-6">
				<div className="flex items-center gap-2 mb-4">
					<div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-50">
						<Users className="h-4 w-4 text-primary-600" />
					</div>
					<h2 className="text-lg font-semibold">Statutární orgány</h2>
				</div>

				{officers.length === 0 ? (
					<p className="text-sm text-muted-foreground">Žádné záznamy o statutárních orgánech</p>
				) : (
					<div className="flex flex-col gap-6">
						{officerGroups.map((group) => (
							<div key={group.key}>
								<h3 className="text-sm font-semibold text-muted-foreground mb-2 border-b border-gray-100 pb-1">
									{group.label}
								</h3>
								<div className="flex flex-col gap-2">
									{group.officers.map((officer) => (
										<OfficerRow key={officer.id} officer={officer} />
									))}
								</div>
							</div>
						))}
					</div>
				)}

				{isAnon && officersInactiveCount > 0 && (
					<AnonHistoryPill
						count={officersInactiveCount}
						label="bývalých statutárů"
						labelOne="bývalý statutár"
						labelFew="bývalí statutáři"
					/>
				)}
			</div>

			{/* Shareholders section */}
			<div className="card-surface rounded-xl p-6">
				<div className="flex items-center gap-2 mb-4">
					<div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-50">
						<Users className="h-4 w-4 text-primary-600" />
					</div>
					<h2 className="text-lg font-semibold">Společníci a akcionáři</h2>
				</div>

				{shareholders.length === 0 ? (
					<p className="text-sm text-muted-foreground">Žádné záznamy o společnících</p>
				) : (
					<div className="flex flex-col gap-3">
						{/* Active shareholders */}
						{activeShareholders.map((sh) => (
							<ShareholderRow key={sh.id} shareholder={sh} />
						))}

						{/* Inactive shareholders - collapsible (registered users only) */}
						{!isAnon && inactiveShareholders.length > 0 && (
							<div>
								<button
									type="button"
									onClick={() => setShowInactive(!showInactive)}
									className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mt-2"
								>
									<ChevronDown
										className={`h-4 w-4 transition-transform ${showInactive ? "rotate-180" : ""}`}
									/>
									{showInactive ? "Skrýt" : "Zobrazit"} neaktivní ({inactiveShareholders.length})
								</button>
								{showInactive && (
									<div className="flex flex-col gap-2 mt-2">
										{inactiveShareholders.map((sh) => (
											<ShareholderRow key={sh.id} shareholder={sh} />
										))}
									</div>
								)}
							</div>
						)}
					</div>
				)}

				{isAnon && shareholdersInactiveCount > 0 && (
					<AnonHistoryPill
						count={shareholdersInactiveCount}
						label="bývalých společníků"
						labelOne="bývalý společník"
						labelFew="bývalí společníci"
					/>
				)}
			</div>
		</div>
	);
}

function AnonHistoryPill({
	count,
	label,
	labelOne,
	labelFew,
}: {
	count: number;
	label: string;
	labelOne: string;
	labelFew: string;
}) {
	const noun = count === 1 ? labelOne : count >= 2 && count <= 4 ? labelFew : label;
	return (
		<Link
			to="/login?mode=register"
			className="mt-4 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-3 py-1.5 text-sm text-primary hover:bg-primary/10 transition-colors"
		>
			<Lock className="h-3.5 w-3.5" />
			<span>
				{count} {noun} — registrujte se zdarma a zobrazte historii
			</span>
		</Link>
	);
}

function OfficerRow({ officer }: { officer: Officer }) {
	const person = officer.redacted ? null : officer.person;

	return (
		<div
			className={`flex items-center gap-3 rounded-lg border border-gray-100 p-3 ${
				!officer.isActive ? "opacity-75 bg-gray-50/50" : ""
			}`}
		>
			<div
				className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
					person == null
						? "bg-gray-100 text-gray-400"
						: officer.isActive
							? "bg-primary-100 text-primary-700"
							: "bg-gray-100 text-gray-500"
				}`}
			>
				{person == null ? <EyeOff className="h-4 w-4" /> : getInitials(person)}
			</div>
			<div className="flex flex-col gap-0.5 min-w-0 flex-1">
				<p className="text-sm font-medium truncate">
					{person == null ? (
						<span className="font-normal italic text-muted-foreground">
							{PERSON_REDACTED_NOTICE}
						</span>
					) : officer.personId ? (
						<Link
							to={personPath(officer.personId, formatPersonName(person))}
							prefetch="intent"
							className="text-primary hover:underline"
						>
							{formatPersonName(person)}
						</Link>
					) : (
						formatPersonName(person)
					)}
				</p>
				<p className="text-xs text-muted-foreground">
					{officer.roleLabel ?? ROLE_LABELS[officer.role] ?? officer.role}
					{officer.appointedAt && ` · od ${formatDate(officer.appointedAt)}`}
					{officer.removedAt && ` · do ${formatDate(officer.removedAt)}`}
				</p>
			</div>
			<span
				className={`shrink-0 ${
					officer.isActive
						? "badge badge-soft badge-success badge-sm"
						: "badge badge-soft badge-error badge-sm"
				}`}
			>
				<span className="hidden sm:inline">{officer.isActive ? "Aktivní" : "Neaktivní"}</span>
				<span className="sm:hidden">{officer.isActive ? "A" : "N"}</span>
			</span>
		</div>
	);
}

/** "Vklad 500 000 Kč · splaceno 100 %" — paidUp is an absolute amount; show it
 * as a percentage of the deposit when computable, as an amount otherwise. */
function formatDeposit(sh: Shareholder): string | null {
	if (sh.shareAmount == null) return null;
	const parts = [`Vklad ${formatAmountWithCurrency(sh.shareAmount, sh.shareCurrency)}`];
	if (sh.paidUp != null) {
		if (sh.shareAmount > 0) {
			const pct = (sh.paidUp / sh.shareAmount) * 100;
			parts.push(`splaceno ${pct.toLocaleString("cs-CZ", { maximumFractionDigits: 1 })} %`);
		} else {
			parts.push(`splaceno ${formatAmountWithCurrency(sh.paidUp, sh.shareCurrency)}`);
		}
	}
	return parts.join(" · ");
}

/** Person shareholders link to /osoba, resolved entity shareholders to /firma. Redacted rows never link. */
function shareholderLinkTo(sh: Shareholder, name: string): string | null {
	if (sh.redacted) return null;
	if (sh.person && sh.personId) return personPath(sh.personId, name);
	if (!sh.person && sh.entityIco) return companyPath(sh.entityIco, sh.entityName ?? "");
	return null;
}

function ShareholderRow({ shareholder: sh }: { shareholder: Shareholder }) {
	const name = sh.redacted
		? PERSON_REDACTED_NOTICE
		: sh.person
			? formatPersonName(sh.person)
			: (sh.entityName ?? "Neznámý subjekt");
	const deposit = formatDeposit(sh);
	const linkTo = shareholderLinkTo(sh, name);
	const details = [
		sh.shareType,
		deposit,
		sh.entityIco ? `IČO: ${sh.entityIco}` : null,
		!sh.entityIco && sh.entityRegNumber ? `Reg.: ${sh.entityRegNumber}` : null,
	]
		.filter(Boolean)
		.join(" · ");

	return (
		<div
			className={`rounded-lg border border-gray-100 p-3 ${
				!sh.isActive ? "opacity-75 bg-gray-50/50" : ""
			}`}
		>
			<div className="flex items-center gap-3">
				<div
					className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
						sh.redacted
							? "bg-gray-100 text-gray-400"
							: sh.isActive
								? "bg-primary-100 text-primary-700"
								: "bg-gray-100 text-gray-500"
					}`}
				>
					{sh.redacted ? (
						<EyeOff className="h-4 w-4" />
					) : sh.person ? (
						getInitials(sh.person)
					) : (
						getInitialsFromName(name)
					)}
				</div>
				<div className="flex flex-col gap-0.5 min-w-0 flex-1">
					<p className="text-sm font-medium truncate">
						{sh.redacted ? (
							<span className="font-normal italic text-muted-foreground">{name}</span>
						) : linkTo ? (
							<Link to={linkTo} prefetch="intent" className="text-primary hover:underline">
								{name}
							</Link>
						) : (
							name
						)}
						{!sh.redacted && sh.shareholderType && (
							<span className="ml-2 text-xs font-normal text-muted-foreground">
								{sh.shareholderType === "AKCIONAR" ? "Akcionář" : "Společník"}
							</span>
						)}
					</p>
					{details && <p className="text-xs text-muted-foreground">{details}</p>}
				</div>
				<span
					className={`shrink-0 ${
						sh.isActive
							? "badge badge-soft badge-success badge-sm"
							: "badge badge-soft badge-error badge-sm"
					}`}
				>
					<span className="hidden sm:inline">{sh.isActive ? "Aktivní" : "Neaktivní"}</span>
					<span className="sm:hidden">{sh.isActive ? "A" : "N"}</span>
				</span>
			</div>

			{/* Ownership bar */}
			{sh.ownershipPercentage != null && (
				<div className="flex items-center gap-3 mt-2 ml-12">
					<div className="h-2 flex-1 rounded-full bg-gray-100">
						<div
							className="h-full rounded-full bg-primary-500"
							style={{ width: `${Math.min(sh.ownershipPercentage, 100)}%` }}
						/>
					</div>
					<span className="text-xs font-mono font-medium tabular-nums shrink-0 w-12 text-right">
						{sh.ownershipPercentage} %
					</span>
				</div>
			)}
		</div>
	);
}
